import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Copy, Download, Trash2, Clock, Tag, Box, Maximize2 } from 'lucide-react';
import { api } from '../lib/api';
import type { VespAsset } from '../types';
import { safeJsonParse } from '../types';
import { useAppStore } from '../store/appStore';

export function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toggleFavorite, deleteAsset } = useAppStore();
  const [asset, setAsset] = useState<VespAsset | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.getAsset(id).then((res) => {
      setAsset(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-500">加载中...</p>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-slate-400">资产未找到</p>
          <button onClick={() => navigate('/')} className="btn-ghost mt-4">返回首页</button>
        </div>
      </div>
    );
  }

  const tags: string[] = safeJsonParse(asset.tags, []);
  const params = safeJsonParse<Record<string, unknown>>(asset.params, {});

  const hash = asset.vesp_id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hue1 = hash % 360;
  const hue2 = (hash * 7) % 360;

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
      >
        <ArrowLeft size={16} />
        返回
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Preview：图片渲染原图（Low#6 大图用原图而非缩略图），视频/音频占位 */}
        <div className="aspect-square rounded-xl overflow-hidden flex items-center justify-center bg-slate-900 relative group">
          {asset.media_type === 'image' && asset.raw_file_name ? (
            <img
              src={`/api/assets/${asset.vesp_id}/file`}
              alt={asset.title}
              className="w-full h-full object-contain"
              onError={(e) => {
                // 原图加载失败 → 回退缩略图，再失败隐藏
                const target = e.target as HTMLImageElement;
                if (target.src.includes('/file')) {
                  target.src = `/thumbs/${asset.vesp_id}.webp`;
                } else {
                  target.style.display = 'none';
                }
              }}
            />
          ) : (
            <>
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, hsl(${hue1}, 40%, 20%) 0%, hsl(${hue2}, 30%, 15%) 100%)`,
                }}
              />
              <span className="relative text-8xl opacity-30">
                {asset.media_type === 'video' ? '🎬' : asset.media_type === 'audio' ? '🎵' : '🖼'}
              </span>
            </>
          )}
        </div>

        {/* Details */}
        <div className="space-y-6">
          {/* Title & Actions */}
          <div>
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-slate-100">{asset.title}</h1>
                <p className="text-sm text-slate-500 mt-1 font-mono">{asset.vesp_id}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleFavorite(asset.vesp_id)}
                  className={`p-2 rounded-lg transition-colors ${
                    asset.is_favorite ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-800 text-slate-400 hover:text-amber-300'
                  }`}
                >
                  <Star size={18} fill={asset.is_favorite ? 'currentColor' : 'none'} />
                </button>
                <button
                  onClick={() => navigator.clipboard.writeText(asset.prompt)}
                  className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-violet-300 transition-colors"
                >
                  <Copy size={18} />
                </button>
                <button className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-blue-300 transition-colors">
                  <Download size={18} />
                </button>
                <button
                  onClick={() => { deleteAsset(asset.vesp_id); navigate('/'); }}
                  className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-red-300 transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Prompt */}
          <div className="glass-card p-4">
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Prompt</h3>
            <p className="text-sm text-slate-200 leading-relaxed">{asset.prompt}</p>
            {asset.negative_prompt && (
              <>
                <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 mt-4">Negative Prompt</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{asset.negative_prompt}</p>
              </>
            )}
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">标签</h3>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span key={tag} className="tag-chip">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4">
            <MetaItem icon={Box} label="分类" value={asset.category} />
            <MetaItem icon={Tag} label="模型" value={asset.model_name || '未知'} />
            <MetaItem icon={Maximize2} label="尺寸" value={asset.width && asset.height ? `${asset.width}×${asset.height}` : '未知'} />
            <MetaItem icon={Clock} label="创建时间" value={asset.created_at?.slice(0, 10) || '未知'} />
          </div>

          {/* Parameters */}
          {Object.keys(params).length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">生成参数</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(params).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-slate-500">{key}</span>
                    <span className="text-slate-300 font-mono">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaItem({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="glass-card p-3 flex items-center gap-3">
      <Icon size={16} className="text-slate-500" />
      <div>
        <p className="text-[11px] text-slate-500">{label}</p>
        <p className="text-sm text-slate-200">{value}</p>
      </div>
    </div>
  );
}
